import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import api, { API } from "../lib/api";

const src = (t) => `${API.replace(/\/api$/, "")}${t.image}`;

// A wall tile is display-only for everyone (no link, no hover affordance).
const Tile = ({ t, fresh = false }) => {
  const pop = fresh ? " animate-pop ring-2 ring-accent" : "";
  return (
    <div data-testid={`wall-tile-${t.id}`} title={t.name} className={`relative shrink-0 overflow-hidden rounded-2xl${t.kind === "duck" ? "" : " opacity-90"}${pop}`}>
      <img src={src(t)} alt={t.name || "printed duck"} loading="lazy" className="h-28 w-28 object-cover rounded-2xl bg-[#f6f4ef] border border-border" />
      {fresh && <span data-testid={`wall-new-${t.id}`} className="absolute left-1.5 top-1.5 rounded-full bg-accent px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#121417] shadow">new</span>}
      <span data-testid={`wall-tag-${t.id}`} className="wall-tag absolute inset-x-0 bottom-0 rounded-b-2xl bg-[#121417]/85 px-2 py-1 text-[10px] leading-tight text-white">
        <span className="block truncate font-semibold">{t.name}</span>
        <span className="block truncate font-mono2 text-[9px] text-[#ffc700]">{t.tag}</span>
      </span>
    </div>
  );
};

// Auto-scrolling wall of finished Duckify ducks padded with real library prints; pauses on hover; new ducks pop in within a minute.
export const PrintedWall = () => {
  const [tiles, setTiles] = useState([]);
  const [fresh, setFresh] = useState(new Set());

  useEffect(() => {
    let alive = true;
    let first = true;
    const load = () =>
      api
        .get("/public/wall", { params: { n: 24 } })
        .then((r) => {
          if (!alive) return;
          setTiles((prev) => {
            const known = new Set(prev.map((t) => t.id));
            const added = first ? [] : r.data.filter((t) => t.kind === "duck" && !known.has(t.id)).map((t) => t.id);
            if (added.length) setFresh(new Set(added));
            return r.data;
          });
          first = false;
        })
        .catch(() => alive && first && setTiles([]));
    load();
    const timer = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  if (tiles.length < 6) return null;
  const strip = (key) => (
    <div key={key} className="flex gap-3 pr-3 shrink-0" aria-hidden={key === "b"}>
      {tiles.map((t) => <Tile key={`${key}-${t.kind}-${t.id}`} t={t} fresh={fresh.has(t.id)} />)}
    </div>
  );
  return (
    <section data-testid="printed-wall" className="mt-6 -mx-4 sm:-mx-8 lg:-mx-12 overflow-hidden">
      <p className="flex items-center gap-2 px-4 sm:px-8 lg:px-12 text-xs font-mono2 uppercase tracking-widest text-muted-foreground mb-3">
        <Printer className="h-4 w-4" /> Recently printed
      </p>
      <div className="duck-marquee flex w-max" style={{ "--marquee-duration": `${Math.max(30, tiles.length * 2.5)}s` }}>
        {strip("a")}
        {strip("b")}
      </div>
    </section>
  );
};
